import { defaultEntityState } from '@app/core/state/entities/entity';
import {
  mergeEntityState,
  removeEntities,
  removeEntitiesByFilter,
  removeEntitiesWithIds,
} from '@app/core/state/entities/entity.helpers';
import { projectDetailActions } from '@app/core/actions/project-detail.actions';
import { stageActions } from '@app/core/actions/stage.actions';
import { createReducer, on } from '@ngrx/store';
import { entityKey } from '../entity-name-map';
import { IMembershipScreeningStats } from '@core/state/entities/membership-screening-stats/membership-screening-stats.entity';
import { INormalizedPayload } from '@core/state/entities/entity';

/**
 * The projects whose full screening statistics this payload carries. A full-stats bundle is the
 * complete row set for its project, so it replaces that project's rows rather than adding to them.
 *
 * #3083: in the anonymised tier the server gives every peer row a surrogate id minted per
 * response, so an upsert would keep the previous response's rows beside the new ones and the
 * leaderboard would list every reviewer again on each refresh or SignalR push. The same
 * replacement clears stale rows when a later bundle carries fewer rows or none (the permission was
 * withdrawn). Payloads without a project screening summary (a project listing, say) carry no full
 * bundle and are merged as before.
 */
export const projectIdsWithFullScreeningStats = (
  props: INormalizedPayload<
    typeof entityKey.membershipScreeningStats | typeof entityKey.projectScreeningStats
  >
): Set<string> =>
  new Set(
    Object.values(props.entities?.projectScreeningStats ?? {})
      .map((summary) => summary?.projectId)
      .filter((projectId): projectId is string => !!projectId)
  );

export const membershipScreeningStatsEntityReducer = createReducer(
  defaultEntityState<IMembershipScreeningStats>(),
  on(
    projectDetailActions.screeningSummaryReceived,
    stageActions.receivedFullStats,
    projectDetailActions.fullProjectStatsReceived,
    projectDetailActions.created,
    projectDetailActions.detailLoaded,
    projectDetailActions.projectForInvitationRetrieved,
    projectDetailActions.requestToJoinProjectResponseReceivedAndGranted,
    projectDetailActions.projectUpdated,
    projectDetailActions.projectListingUpdated,
    (state, props) => {
      const replacedProjectIds = projectIdsWithFullScreeningStats(props);
      return mergeEntityState(
        entityKey.membershipScreeningStats,
        state,
        props,
        replacedProjectIds.size
          ? (row) => replacedProjectIds.has(row.projectId)
          : undefined
      );
    }
  ),
  on(projectDetailActions.projectDeleted, (state, props) =>
    removeEntitiesByFilter(
      state,
      (projChild) => projChild.projectId === props.projectId
    )
  )
);
